import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Users, Clock, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForumPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API}/forum/categories`);
        if (response.ok) {
          const data = await response.json();
          setCategories(data);
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'No posts yet';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const iconMap = {
    MessageSquare: MessageSquare,
    Gamepad2: MessageSquare,
    Palette: MessageSquare,
    Code: MessageSquare,
  };

  return (
    <div className="space-y-6" data-testid="forum-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <MessageSquare size={32} className="text-[#cc0000]" />
        <div>
          <h1 className="text-2xl font-black text-white">BBS</h1>
          <p className="text-xs text-[#666]">WarSyndicate Bulletin Board System</p>
        </div>
      </div>

      {/* Categories */}
      {loading ? (
        <div className="space-y-4">
          {Array(4).fill(0).map((_, i) => (
            <div key={i} className="bg-[#111] border border-[#333] animate-pulse">
              <div className="h-12 bg-[#1a0000]" />
              <div className="p-4 space-y-3">
                <div className="h-8 bg-[#1a1a1a]" />
                <div className="h-8 bg-[#1a1a1a]" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4" data-testid="forum-categories">
          {categories.map((category) => {
            const IconComponent = iconMap[category.icon] || MessageSquare;
            
            return (
              <div key={category.category_id} className="ws-forum-category">
                {/* Category header */}
                <div className="ws-forum-category-header flex items-center gap-3">
                  <IconComponent size={20} className="text-[#cc0000]" />
                  <div>
                    <h2 className="text-sm font-bold text-white uppercase">{category.name}</h2>
                    <p className="text-xs text-[#666]">{category.description}</p>
                  </div>
                </div>

                {/* Subforums */}
                <div>
                  {category.subforums?.map((subforum) => (
                    <Link 
                      key={subforum.subforum_id}
                      to={`/bbs/${subforum.subforum_id}`}
                      className="ws-subforum-row"
                      data-testid={`subforum-${subforum.subforum_id}`}
                    >
                      {/* Icon */}
                      <div className="w-10 h-10 bg-[#1a1a1a] border border-[#333] flex items-center justify-center mr-4">
                        <MessageSquare size={18} className="text-[#666]" />
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-bold text-white hover:text-[#ffcc00] transition-colors">
                          {subforum.name}
                        </h3>
                        <p className="text-xs text-[#666] truncate">{subforum.description}</p>
                      </div>

                      {/* Stats */}
                      <div className="hidden md:flex items-center gap-6 text-xs text-[#666] mr-4">
                        <div className="text-center">
                          <div className="text-[#999] mono">{subforum.thread_count || 0}</div>
                          <div>Threads</div>
                        </div>
                        <div className="text-center">
                          <div className="text-[#999] mono">{subforum.post_count || 0}</div>
                          <div>Posts</div>
                        </div>
                      </div>

                      {/* Last post */}
                      <div className="hidden lg:block w-48 text-xs">
                        {subforum.last_post_date ? (
                          <>
                            <div className="text-[#999] flex items-center gap-1">
                              <Clock size={10} />
                              {formatDate(subforum.last_post_date)}
                            </div>
                            <div className="text-[#666]">
                              by <span className="text-[#ffcc00]">{subforum.last_post_author}</span>
                            </div>
                          </>
                        ) : (
                          <span className="text-[#444]">No posts yet</span>
                        )}
                      </div>

                      <ChevronRight size={16} className="text-[#333]" />
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Forum stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="ws-stat-box">
          <div className="ws-stat-value">{categories.length}</div>
          <div className="ws-stat-label">Categories</div>
        </div>
        <div className="ws-stat-box">
          <div className="ws-stat-value">
            {categories.reduce((acc, cat) => acc + (cat.subforums?.length || 0), 0)}
          </div>
          <div className="ws-stat-label">Subforums</div>
        </div>
        <div className="ws-stat-box">
          <div className="ws-stat-value">
            {categories.reduce((acc, cat) => 
              acc + (cat.subforums?.reduce((a, s) => a + (s.thread_count || 0), 0) || 0), 0)}
          </div>
          <div className="ws-stat-label">Threads</div>
        </div>
        <div className="ws-stat-box">
          <div className="ws-stat-value">
            {categories.reduce((acc, cat) => 
              acc + (cat.subforums?.reduce((a, s) => a + (s.post_count || 0), 0) || 0), 0)}
          </div>
          <div className="ws-stat-label">Posts</div>
        </div>
      </div>
    </div>
  );
}
